/**
 * The one place that understands a 429 from sphere-mcp.
 *
 * The server's budget is PER USER, enforced in an `onRequest` hook that every authenticated route
 * opts into (`per-user-rate-limit.ts` / `early-auth.ts` over there) — so a refusal can come back on
 * any of the four wires this app opens: the MCP endpoint, the chat transport, the conversations
 * REST routes, and file uploads. It used to be understood on exactly one of them, which is why this
 * module exists rather than a second copy of the parser next to each `fetch`.
 *
 * The window is FIXED, not a token bucket. That is the whole reason to treat a 429 differently from
 * any other failure: retrying before the window rolls over does not merely fail, it spends another
 * refusal and pushes nothing forward. `Retry-After` names the moment it is worth asking again, and
 * a caller that ignores it is strictly worse than one that waits.
 *
 * CROSS-ORIGIN NOTE, and the reason this was dead code for a while: `Retry-After` is not a
 * CORS-safelisted response header, so page script reads `null` for it unless the server names it in
 * `Access-Control-Expose-Headers`. sphere-mcp does (`cors.ts`, `exposedHeaders`). If that entry is
 * ever dropped, nothing here errors — every 429 just silently loses its delay and every caller
 * quietly falls back to its own backoff. That is the failure mode to remember when this looks
 * inexplicably conservative.
 */

/**
 * Thrown when sphere-mcp refused a request to protect its per-user budget (HTTP 429).
 *
 * Typed rather than generic so a caller can tell "come back later" apart from "this failed":
 * the two want opposite responses, and only the server knows how much later.
 *
 * `retryAfterMs` is undefined when no usable header came back, which callers must read as "use your
 * own backoff" — never as "retry now".
 */
export class SphereAIRateLimitedError extends Error {
	constructor(readonly retryAfterMs?: number) {
		super("Sphere AI request was rate limited");
		this.name = "SphereAIRateLimitedError";
	}
}

/**
 * `Retry-After` as milliseconds, or undefined if there is nothing usable to read.
 *
 * RFC 9110 § 10.2.3 allows two forms and both are handled: delta-seconds (what sphere-mcp sends)
 * and an HTTP-date (what a proxy in between may rewrite it to).
 *
 * Getting this wrong in the LENIENT direction is the expensive mistake — a failed parse that fell
 * through to "retry immediately" would hammer a server that had just asked us to wait — so anything
 * unrecognised yields undefined and leaves the caller on its own backoff.
 */
export function parseRetryAfterMs(header: string | null): number | undefined {
	if (!header?.trim()) return undefined;
	const seconds = Number(header);
	// `Number` is used rather than parseFloat on purpose: it rejects "5 minutes"
	// (NaN) instead of quietly reading 5 out of it.
	if (Number.isFinite(seconds)) return Math.max(0, seconds * 1_000);
	const at = Date.parse(header);
	if (Number.isNaN(at)) return undefined;
	// A date already in the past means "now" — never a negative delay.
	return Math.max(0, at - Date.now());
}

/** The typed refusal for a 429 response, with whatever delay the server managed to tell us. */
export function rateLimitedFrom(response: Response): SphereAIRateLimitedError {
	return new SphereAIRateLimitedError(parseRetryAfterMs(response.headers.get("Retry-After")));
}

/**
 * How long a caller should wait before trying again, given its own cadence and the error it just
 * saw. The server's delay wins only when it is LONGER: a server asking us back in 3s cannot talk a
 * caller into going faster than the interval it chose for itself, and longer is the only direction
 * that can actually help. That asymmetry is what makes this safe to apply blindly to any failure.
 */
export function backoffMsFor(intervalMs: number, err: unknown): number {
	const retryAfterMs = err instanceof SphereAIRateLimitedError ? err.retryAfterMs : undefined;
	return Math.max(intervalMs, retryAfterMs ?? 0);
}

/**
 * The delay to SHOW a waiting human, in whole seconds, or null when the server named none.
 *
 * Rounded up: telling someone to wait 4s when the window has 4.2s left invites exactly the early
 * retry this whole module exists to prevent. A named delay is always at least 1s for the same
 * reason — "wait 0 seconds" reads as "go ahead now".
 */
export function retryAfterSeconds(retryAfterMs: number | undefined): number | null {
	if (retryAfterMs === undefined) return null;
	return Math.max(1, Math.ceil(retryAfterMs / 1_000));
}
